import React, { FC, memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import SearchInput from '@minecraft.search-input';
import Button from '@minecraft.button';
import { DropdownWithChildrenProps, DropdownTogglerTypeEnum } from '../shared/types';
import { useDropdownWithChildrenContext } from '../shared/context';
import { useFetchMoreOptions } from '../shared/hooks';
import DropdownList from '../DropdownWithChildrenDropdownList';

const StyledDropdownListHead = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.625rem;
`;

const StyledSeeMoreButton = styled(Button)`
  align-self: flex-end;
  color: ${(props): string => props.theme.colors.buttons.primary.default};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0;
  height: auto;
  min-width: unset;
  margin-top: 0.375rem;

  &,
  &:hover,
  &:focus,
  &:active,
  &:not(:disabled):not(.disabled):active {
    color: ${(props): string => props.theme.colors.buttons.primary.hover};
    text-decoration: none;
  }
`;

type Props = Partial<DropdownWithChildrenProps>;

const DropdownWithChildrenDropdownListWithSearch: FC<Props> = ({
  withAutocomplete,
  withSelectAll,
  withSeeMoreButton,
  heightDropdownAuto,
  isLoading,
  isMultipleSelect,
  isCollapsable,
  dropdownTogglerType,
  selectionsLimit,
  initialOffset,
  initialLimit,
  optionsTotalCount,
  options,
  onOptionsFetch,
  onOptionsFetchMore,
  dataTestId,
}) => {
  const { t } = useTranslation();

  const { searchInputValue, handleSearchInputValueChange, handleSearchInputValueClear } =
    useDropdownWithChildrenContext();
  const { handleOptionsFetchMore } = useFetchMoreOptions({
    initialLimit,
    optionsTotalCount,
    options,
    onOptionsFetchMore,
  });

  return (
    <>
      {(dropdownTogglerType === DropdownTogglerTypeEnum.button || withAutocomplete) && (
        <StyledDropdownListHead>
          {dropdownTogglerType === DropdownTogglerTypeEnum.button && (
            <SearchInput
              name="dropdown-search-input"
              placeholder={t('common:form.label.search')}
              value={searchInputValue}
              onChange={handleSearchInputValueChange}
              onClear={handleSearchInputValueClear}
            />
          )}
          {withAutocomplete && withSeeMoreButton && (
            <StyledSeeMoreButton
              color="link"
              disabled={isLoading || optionsTotalCount <= options?.length}
              onClick={handleOptionsFetchMore}
            >
              {t('common:button.seeMore')}
            </StyledSeeMoreButton>
          )}
        </StyledDropdownListHead>
      )}
      <DropdownList
        withSelectAll={withSelectAll}
        withAutocomplete={withAutocomplete}
        heightDropdownAuto={heightDropdownAuto}
        isLoading={isLoading}
        isMultipleSelect={isMultipleSelect}
        isCollapsable={isCollapsable}
        selectionsLimit={selectionsLimit}
        initialOffset={initialOffset}
        initialLimit={initialLimit}
        options={options}
        onOptionsFetch={onOptionsFetch}
        dataTestId={dataTestId}
      />
    </>
  );
};

export default memo(DropdownWithChildrenDropdownListWithSearch);
